import React, { useMemo, useState } from "react";
import useArticles from "../hooks/useArticles";
import Container from "../components/Container";

export default function News() {
  const all = useArticles();
  const clean = (s) => (s || "").toLowerCase().trim();
  const isNews = (a) => clean(a.category) === "news";

  const getTags = (a) =>
    (a.tags || a.Tags || "")
      .split(",")
      .map((t) => clean(t))
      .filter(Boolean);

  const news = useMemo(() => all.filter(isNews), [all]);

const TAG_OPTIONS = [
  "2025",
  "Market Insights",
  "Tax",
  "Mortgages",
  "Forecasts",
  "Policy",
  "Interest Rates",
  "Planning",
  "Investment Zones",
  "UK Cities",
];


  const [selectedTags, setSelectedTags] = useState([]);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("Newest");

  const toggleTag = (t) =>
    setSelectedTags((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

  const filtered = useMemo(() => {
    const q = clean(query);
    let arr = news.filter((a) => {
      const tags = getTags(a);
      const tagMatch =
        selectedTags.length === 0 ||
        selectedTags.every((t) => tags.includes(t));
      const queryMatch =
        !q ||
        clean(a.title).includes(q) ||
        clean(a.excerpt).includes(q);
      return tagMatch && queryMatch;
    });
    return arr.sort((a, b) =>
      sort === "Newest"
        ? new Date(b.date) - new Date(a.date)
        : new Date(a.date) - new Date(b.date)
    );
  }, [news, query, selectedTags, sort]);

  return (
    <main>
      <section className="relative overflow-hidden">
        <img
          src="/content/images/news-hero.jpg"
          alt="News header"
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-white/30 to-white" />
        <Container className="relative py-16 sm:py-20">
          <h1 className="text-4xl font-bold text-[#003366]">Property News</h1>
          <p className="mt-3 text-[#003366] max-w-2xl">
            Browse updates on UK investment trends, planning policy, interest
            rates, regional growth and more.
          </p>
        </Container>
      </section>

      <section>
        <Container className="py-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search headlines…"
              className="w-full sm:w-80 rounded-xl border border-slate-300 px-3 py-2"
            />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-2"
            >
              <option>Newest</option>
              <option>Oldest</option>
            </select>
          </div>

         {/* Static filter buttons */}
{/* Static, capitalised topic chips (currently disabled)
    <div>
      <div className="text-sm font-semibold text-[#003366] mb-2">
        Filter by topic
      </div>
      <div className="flex flex-wrap gap-2">
        {TAG_OPTIONS.map((label) => {
          const slug = label.toLowerCase();
          const active = selectedTags.includes(slug);
          return (
            <button
              key={slug}
              onClick={() => toggleTag(label)}
              className={`rounded-full border px-3 py-1 text-sm font-medium capitalize ${
                active
                  ? "bg-slate-900 text-white border-slate-900"
                  : "border-slate-300 text-[#003366] hover:bg-slate-50"
              }`}
            >
              {label}
            </button>
          );
        })}
        {selectedTags.length > 0 && (
          <button
            onClick={() => setSelectedTags([])}
            className="ml-2 rounded-full border border-slate-300 px-3 py-1 text-sm text-[#003366] hover:bg-slate-50"
          >
            Clear
          </button>
        )}
      </div>
    </div>
    */}

          
        </Container>
      </section>

      <section>
        <Container className="pb-16">
          {filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 p-6 text-[#003366]">
              No matching news articles.
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a) => (
                <article
                  key={a.id}
                  className="rounded-3xl bg-white shadow-sm ring-1 ring-slate-200 overflow-hidden"
                >
                  <img
                    src={a.image}
                    alt=""
                    className="h-48 w-full object-cover"
                  />
                  <div className="p-6">
                    <div className="text-xs font-semibold uppercase tracking-wide text-[#003366]">
                      {a.category}
                    </div>
                    <h3 className="mt-1 text-lg font-semibold text-[#003366]">
                      {a.title}
                    </h3>
                    <p className="mt-2 text-[#003366]">{a.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between text-sm text-[#003366]">
                      <time dateTime={a.date}>
                        {new Date(a.date).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </time>
                      <a
                        href={`#/article/${a.slug}`}
                        className="font-semibold text-[#003366]"
                      >
                        Read more →
                      </a>
                    </div>
                    {/* Tags */}
                    {getTags(a).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {getTags(a).map((t) => (
                          <span
                            key={t}
                            className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-[#003366]"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </Container>
      </section>
    </main>
  );
}
